// Terms and Conditions Page
import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import { usePageMeta } from "../components/shared/SEOHelper";

const TermsPage = () => {
  usePageMeta({
    title: "Terms and Conditions | Annelie Lily Yoga | Frome, Somerset",
    description: "Terms and conditions for using the Annelie Lily Yoga website. Read our rules, intellectual property rights, and liability information for yoga classes and",
    canonical: "https://annelielily.com/terms"
  });

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      
      <main className="pt-28 pb-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl md:text-4xl text-neutral-800 mb-8">
            Terms and Conditions
          </h1>
          
          <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">
            <p className="font-semibold">
              PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY BEFORE USING THIS SITE
            </p>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">What's in these terms?</h2>
              <p>
                These terms tell you the rules for using our website annelielily.com (<strong>our site</strong>).
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">Who we are and how to contact us</h2>
              <p>
                Our site is operated by Seventh Sense Healing Limited ("We"). We are registered in England and Wales 
                under company number 10800506 and have our registered office at C/O Prydis, Senate Court, Southernhay 
                Gardens, Exeter, England, EX1 1NT. We are a limited company.
              </p>
              <p>
                To contact us, please email <a href="mailto:seventhsensehealing@outlook.com" className="text-gold hover:underline">seventhsensehealing@outlook.com</a> or 
                telephone our office on <a href="tel:07540995021" className="text-gold hover:underline">07540 995021</a>.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">By using our site you accept these terms</h2>
              <p>
                By using our site, you confirm that you accept these terms of use and that you agree to comply with them.
                If you do not agree to these terms, you must not use our site.
              </p>
              <p>We recommend that you print a copy of these terms for future reference.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">There are other terms that may apply to you</h2>
              <p>These terms of use refer to the following additional terms, which also apply to your use of our site:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Our <Link to="/privacy-policy" className="text-gold hover:underline">Privacy Policy</Link></li>
                <li>Our <Link to="/cookie-policy" className="text-gold hover:underline">Cookie Policy</Link> which sets out information about the cookies on our site.</li>
              </ul>
              <p className="mt-4">If you:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Purchase goods from our site, our terms and conditions of supply apply;</li>
                <li>Sign up to our courses then our course terms and conditions will apply;</li>
                <li>Become a member then our membership terms will apply.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">We may make changes to these terms</h2>
              <p>
                We amend these terms from time to time. Every time you wish to use our site, please check these terms 
                to ensure you understand the terms that apply at that time.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">We may make changes to our site</h2>
              <p>
                We may update and change our site from time to time to reflect changes to our products, our users' needs 
                and our business priorities.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">We may suspend or withdraw our site</h2>
              <p>
                Our site is made available free of charge unless you are a member in which case you are subject to our 
                membership terms and conditions or you buy a course and you are subject to our course terms and conditions.
              </p>
              <p>
                We do not guarantee that our site, or any content on it, will always be available or be uninterrupted. 
                We may suspend or withdraw or restrict the availability of all or any part of our site for business and 
                operational reasons. We will try to give you reasonable notice of any suspension or withdrawal.
              </p>
              <p>
                You are also responsible for ensuring that all persons who access our site through your internet connection 
                are aware of these terms of use and other applicable terms and conditions, and that they comply with them.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">You must keep your account details safe</h2>
              <p>
                If you choose, or you are provided with, a user identification code, password or any other piece of 
                information as part of our security procedures, you must treat such information as confidential. You must 
                not disclose it to any third party.
              </p>
              <p>
                We have the right to disable any user identification code or password, whether chosen by you or allocated 
                by us, at any time, if in our reasonable opinion you have failed to comply with any of the provisions of 
                these terms of use.
              </p>
              <p>
                If you know or suspect that anyone other than you knows your user identification code or password, you must 
                promptly notify us at seventhsensehealing@outlook.com.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">How you may use material on our site</h2>
              <p>
                We are the owner or the licensee of all intellectual property rights in our site, and in the material 
                published on it. Those works are protected by copyright laws and treaties around the world. All such 
                rights are reserved.
              </p>
              <p>
                You may print off one copy, and may download extracts, of any page(s) from our site for your personal use 
                and you may draw the attention of others within your organisation to content posted on our site.
              </p>
              <p>
                You must not modify the paper or digital copies of any materials you have printed off or downloaded in any 
                way, and you must not use any illustrations, photographs, video or audio sequences or any graphics separately 
                from any accompanying text.
              </p>
              <p>
                Our status (and that of any identified contributors) as the authors of content on our site must always be acknowledged.
              </p>
              <p>
                You must not use any part of the content on our site for commercial purposes without obtaining a licence to 
                do so from us or our licensors.
              </p>
              <p>
                If you print off, copy or download any part of our site in breach of these terms of use, your right to use 
                our site will cease immediately and you must, at our option, return or destroy any copies of the materials 
                you have made.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">Do not rely on information on this site</h2>
              <p>
                The content on our site is provided for general information only. It is not intended to amount to advice 
                on which you should rely. You must obtain professional or specialist advice before taking, or refraining 
                from, any action on the basis of the content on our site.
              </p>
              <p>
                Although we make reasonable efforts to update the information on our site, we make no representations, 
                warranties or guarantees, whether express or implied, that the content on our site is accurate, complete 
                or up to date.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">We are not responsible for websites we link to</h2>
              <p>
                Where our site contains links to other sites and resources provided by third parties, these links are 
                provided for your information only. Such links should not be interpreted as approval by us of those linked 
                websites or information you may obtain from them.
              </p>
              <p>We have no control over the contents of those sites or resources.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">Our responsibility for loss or damage suffered by you</h2>
              <p><strong>Whether you are a consumer or a business user:</strong></p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  We do not exclude or limit in any way our liability to you where it would be unlawful to do so. This 
                  includes liability for death or personal injury caused by our negligence or the negligence of our employees, 
                  agents or subcontractors and for fraud or fraudulent misrepresentation.
                </li>
                <li>
                  Different limitations and exclusions of liability will apply to liability arising as a result of the supply 
                  of any products to you, which will be set out in our terms and conditions of supply.
                </li>
                <li>
                  Please note that we only provide our site for domestic and private use. You agree not to use our site for 
                  any commercial or business purposes, and we have no liability to you for any loss of profit, loss of business, 
                  business interruption, or loss of business opportunity.
                </li>
                <li>
                  If defective digital content that we have supplied, damages a device or digital content belonging to you and 
                  this is caused by our failure to use reasonable care and skill, we will either repair the damage or pay you 
                  compensation.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">How we may use your personal information</h2>
              <p>
                We will only use your personal information as set out in our <Link to="/privacy-policy" className="text-gold hover:underline">Privacy Policy</Link>.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">We are not responsible for viruses and you must not introduce them</h2>
              <p>
                We do not guarantee that our site will be secure or free from bugs or viruses. You are responsible for 
                configuring your information technology, computer programmes and platform to access our site. You should 
                use your own virus protection software.
              </p>
              <p>
                You must not misuse our site by knowingly introducing viruses, trojans, worms, logic bombs or other material 
                that is malicious or technologically harmful. You must not attempt to gain unauthorised access to our site, 
                the server on which our site is stored or any server, computer or database connected to our site. You must 
                not attack our site via a denial-of-service attack or a distributed denial-of service attack.
              </p>
              <p>
                By breaching this provision, you would commit a criminal offence under the Computer Misuse Act 1990. We will 
                report any such breach to the relevant law enforcement authorities and we will co-operate with those authorities 
                by disclosing your identity to them. In the event of such a breach, your right to use our site will cease immediately.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">Rules about linking to our site</h2>
              <p>
                You may link to our home page, provided you do so in a way that is fair and legal and does not damage our 
                reputation or take advantage of it.
              </p>
              <p>
                You must not establish a link in such a way as to suggest any form of association, approval or endorsement 
                on our part where none exists.
              </p>
              <p>You must not establish a link to our site in any website that is not owned by you.</p>
              <p>
                Our site must not be framed on any other site, nor may you create a link to any part of our site other than 
                the home page.
              </p>
              <p>We reserve the right to withdraw linking permission without notice.</p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">Which country's laws apply to any disputes?</h2>
              <p>
                If you are a consumer, please note that these terms of use, their subject matter and their formation, are 
                governed by English law. You and we both agree that the courts of England and Wales will have exclusive 
                jurisdiction except that if you are a resident of Northern Ireland you may also bring proceedings in 
                Northern Ireland, and if you are resident of Scotland, you may also bring proceedings in Scotland.
              </p>
              <p>
                If you are a business, these terms of use, their subject matter and their formation (and any non-contractual 
                disputes or claims) are governed by English law. We both agree to the exclusive jurisdiction of the courts 
                of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl text-neutral-800 mt-8 mb-4">Our trade marks</h2>
              <p>
                Seventh Sense and Yoga for Children are unregistered trademarks of Seventh Sense Healing Limited. You are 
                not permitted to use them without our approval. Please contact us at seventhsensehealing@outlook.com if you 
                wish to use our unregistered trademarks.
              </p>
            </section>

            <div className="mt-12 pt-8 border-t border-gold/20">
              <p className="text-sm text-neutral-500">
                Last updated: December 2025
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
